import { Title } from '@angular/platform-browser';
import { DwAppInitService } from '@devwareapps/devware-cap';
import { AppDevwareConfigProviderService } from './app-devware-config-provider.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { GuardsCheckEnd, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { DwAuthenticationType, DwMenuHelperService, DwMenuService, DwMenuServiceState, DwSecurityUserService } from '@devwareapps/devware-cap';
import { environment } from 'src/environments/environment';
import { delay, distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  isAdminLayout = false;
  private readonly destroyed$ = new Subject<void>();
  private readonly onScroll = () => {
    const scrolled = (document.scrollingElement?.scrollTop || 0) > 50;
    if (scrolled !== this.scrolled) {
      this.scrolled = scrolled;
      this.setTheme(this.menuService.menuState);
    }
  };
  showLayout: boolean = true;
  showSlashScreen: boolean = true;
  environmentMessage: string;
  scrolled = false;
  themeClasses: string;
  logoutComplete: boolean;

  constructor(
    private router: Router,
    private menuService: DwMenuService,
    private menuHelperService: DwMenuHelperService,
    private dwSecurityUserService: DwSecurityUserService,
    private changeDetector: ChangeDetectorRef,
    private titleService: Title,
    private appInitService: DwAppInitService,
  ) {
    this.environmentMessage = environment.environmentMessage;
  }

  ngOnInit(): void {
    this.titleService.setTitle(AppDevwareConfigProviderService.getEnvironmentBaseTitle());
    this.appInitService.getAppInitResult().pipe(take(1), takeUntil(this.destroyed$)).subscribe(result => {
      this.titleService.setTitle(AppDevwareConfigProviderService.getEnvironmentBaseTitleForRuntime(result?.EnvironmentInfo));
    });
    this.updateShell(this.router.url);
    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe(event => {
      if (event instanceof GuardsCheckEnd && event.shouldActivate) {
        // Register the destination shell's outlet before Angular activates routes.
        // Swapping at NavigationEnd recreates CAP parent/child outlets too late.
        this.updateShell(event.urlAfterRedirects);
        this.changeDetector.detectChanges();
      } else if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.updateShell(this.router.url);
        this.changeDetector.detectChanges();
      }
      if (event instanceof NavigationEnd) {
        this.updateShell(event.urlAfterRedirects);
        this.setTheme(this.menuService.menuState);
      }
    });
    setTimeout(() => {
      this.showSlashScreen = false;
    }, 200);

    document.addEventListener("scroll", this.onScroll);

    this.menuService.menuStateObservable
      .pipe(delay(10), takeUntil(this.destroyed$))
      .subscribe(menuState => {
        this.setTheme(menuState);
      });

    this.setTheme(this.menuService.menuState);

    this.dwSecurityUserService.securityContextChanged()
      .pipe(map((securityContext) => {
        return securityContext?.ApplicationUser?.UserId;
      }), distinctUntilChanged(),
        delay(10), takeUntil(this.destroyed$))
      .subscribe(() => {
        // TODO: Handle security change for fitness-specific logic
      });
  }

  private updateShell(url: string): void {
    const segments = this.router.parseUrl(url).root.children.primary?.segments || [];
    // Administration inside Central keeps the participant shell and selection.
    this.isAdminLayout = segments[0]?.path.toLowerCase() !== 'streaker-central' && segments.some(segment => {
      const path = segment.path.toLowerCase();
      return path === 'admin' || path === 'streaker-admin';
    });
  }

  private setTheme(menuState: DwMenuServiceState) {
    const currentMenu = menuState.currentMenu;

    let themeClass: string = Themes.home;
    let themeClasses = themeClass;

    if (!currentMenu) {
      if (this.scrolled) {
        themeClasses += ',scrolled';
      }
      this.setThemeFinal(themeClasses);
      return;
    }

    themeClass = Themes.main;

    if (this.isAdminLayout) {
      themeClass = Themes.admin;
    }

    if (currentMenu.data?.referenceKey == 'home') {
      themeClass = Themes.home;
    }

    themeClasses = themeClass;

    if (this.scrolled) {
      themeClasses += ',scrolled';
    }

    this.setThemeFinal(themeClasses);

    if (!this.dwSecurityUserService.securityState.isAuthenticated) {

      if (this.dwSecurityUserService.securityState.authenticationType == DwAuthenticationType.logout && !this.logoutComplete) {

        setTimeout(() => {
          themeClasses += ',home-page-annonymous';
          this.setThemeFinal(themeClasses);
          this.logoutComplete = true;
        }, 250);
        return;
      } else {
        themeClasses += ',home-page-annonymous';
        this.setThemeFinal(themeClasses);
      }
    } else {
      this.logoutComplete = false;
    }
  }

  private setThemeFinal(themeClass: string) {
    setTimeout(() => {
      this.themeClasses = themeClass;
    }, 0);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onScroll);
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}

enum Themes {
  home = 'home-theme',
  main = 'main-theme',
  admin = 'admin-theme'
}
